import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Folder } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Divider, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { AgGridReact } from "ag-grid-react";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import {
  selectCurrentAccount,
  useCarveMultiProjectMutation,
  useEstimateCarveMultiProjectQuotaUsageMutation,
  useGetAccountSubscriptionsQuery,
  useGetMultiProjectQuery,
  useGetMultiProjectStatisticsQuery,
} from "fond/api";
import { CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN } from "fond/constants";
import { useProjectQuota } from "fond/project/solve/hooks/useProjectQuota";
import UsageProgress from "fond/project/solve/UsageProgress";
import { MultiProject, MultiProjectArea, SubscriptionStatus } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel } from "fond/widgets";
import DataGridToolbar from "fond/widgets/DataGrid/DataGridToolbar";

import { SubareaList } from "../SubareaList";

import { ViewButton } from "./CarvePanel.styles";

interface CarvePanelProps {
  progress?: number | null;
  multiProject: MultiProject;
}

const CarvePanel: React.FC<CarvePanelProps> = ({ progress = null, multiProject }: CarvePanelProps) => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<MultiProjectArea>>(null);
  const { isLoading, isFetching } = useGetMultiProjectQuery(multiProject.ID);
  const [carveMultiProject, { isLoading: isCarving, isError }] = useCarveMultiProjectMutation();
  const areasWithProjects = useMemo(() => multiProject?.Areas.filter((area) => area.Project?.ID).map(({ ID }) => ID), [multiProject]);
  const [selected, setSelected] = useState<string[]>([]);
  const hasExceededMaxSubareaCount = selected.length > CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN;
  const allProjectsCreated = multiProject?.Areas.length === areasWithProjects.length;
  const isCreatingProjects = progress !== null && progress !== 1;

  const { data: statistics, isLoading: isLoadingStatistics } = useGetMultiProjectStatisticsQuery(multiProject.ID ?? skipToken);
  const selectedAccount = useAppSelector(selectCurrentAccount);
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(selectedAccount?.ID ?? skipToken);
  const activeSubscription = useMemo(() => subscriptions?.find((sub) => sub.Status === SubscriptionStatus.Active && !!sub.Planner), [subscriptions]);
  const subscriptionDetails = useProjectQuota(multiProject);

  const [estimateCarveMultiProjectQuotaUsage, { data: estimatedQuotaUsage, reset: resetEstimatedQuotaUsage, isLoading: isEstimatingQuotaUsage }] =
    useEstimateCarveMultiProjectQuotaUsageMutation();

  useEffect(
    () => {
      if (selected.length === 0) {
        // The backend rejects estimate requests with no selected areas, so clear the data to make sure we're consistent.
        resetEstimatedQuotaUsage();
        return;
      }
      estimateCarveMultiProjectQuotaUsage({ ID: multiProject.ID, Areas: selected.map((ID) => ({ ID })) }).catch((error) => {
        throw new Error("Failed to estimate carve multi project quota usage");
      });
    },
    // resetEstimatedQuotaUsage goes infinite if included in the dependency array; really the only
    // thing we actually care about is `selected` changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [estimateCarveMultiProjectQuotaUsage, multiProject, selected]
  );

  const handleOnClick = async () => {
    if (multiProject && selected.length > 0) {
      try {
        await carveMultiProject({ ID: multiProject.ID, Areas: selected.map((ID) => ({ ID })) });
        analytics.track("project.market-lens.plan.projects.created", {
          selectedAreaCount: selected.length,
          totalAreaCount: multiProject.Areas.length,
          previouslyGeneratedAreaCount: areasWithProjects.length,
          projectId: multiProject.ID,
        });
        setSelected([]);
      } catch (error) {
        throw new Error("Failed to create projects");
      }
    }
  };

  const handleOnViewButtonClick = () => {
    navigate(`/cities/${multiProject?.Folder.ID}`);
  };

  const handleOnSelectionChanged = useCallback(() => {
    if (gridRef.current?.api) {
      const rowIds = gridRef.current?.api.getSelectedRows().map(({ ID }) => ID);
      setSelected(rowIds);
    }
  }, []);

  const totalPremCount: number = estimatedQuotaUsage?.EstimatedQuotaUsage ?? 0;
  const availableQuota: number = subscriptionDetails?.AvailableQuota ?? 0;
  const overPremCount = availableQuota - totalPremCount < 0;

  // Treat a non-empty selection with no estimate yet as a loading/blocked state.
  const isEstimatePendingForSelection = selected.length > 0 && estimatedQuotaUsage == null;

  const loading = isLoading || isFetching || isCarving || isLoadingStatistics || isEstimatingQuotaUsage || isEstimatePendingForSelection;

  return (
    <>
      {areasWithProjects.length === 0 && <Typography variant="subtitle2">No plan generated</Typography>}

      {isCreatingProjects && (
        <Alert
          severity="info"
          variant="compact"
          sx={{
            mb: 2,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <AlertTitle>Creating projects...</AlertTitle>
          <LinearProgressWithLabel value={progress * 100} variant="determinate" />
        </Alert>
      )}

      {areasWithProjects.length > 0 && (
        <>
          <Typography variant="subtitle2">Projects created</Typography>
          <Typography variant="caption">{areasWithProjects.length} subarea projects have been created</Typography>
          <Divider sx={{ mt: 2 }} />
          <ViewButton startIcon={<Folder color="primary" />} onClick={handleOnViewButtonClick} fullWidth>
            View market lens projects
          </ViewButton>
          <Divider />
        </>
      )}

      {!allProjectsCreated && (
        <>
          {areasWithProjects.length > 0 && (
            <Typography variant="subtitle2" sx={{ mt: 3 }}>
              Generate additional projects
            </Typography>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Confirm the subareas you want to include in your market lens plan.</Typography>
          </Box>

          <Box sx={{ pl: 2, pr: 1 }}>
            <DataGridToolbar selected={selected.length} title={`${selected?.length} subareas`} size="small" />
          </Box>
          <SubareaList
            multiProject={multiProject}
            areaStatistics={statistics?.Areas}
            onSelectionChanged={handleOnSelectionChanged}
            ref={gridRef}
            previouslySelectedIds={areasWithProjects}
            selectedIds={selected}
            loading={loading}
          />
        </>
      )}

      {isError && (
        <Alert sx={{ mt: 1 }} severity="error" variant="compact">
          <AlertTitle sx={{ fontSize: (theme) => theme.typography.size.normal, mb: 0 }}>Error creating subarea projects</AlertTitle>
          Something went wrong with creating the subarea projects. Please try again.
        </Alert>
      )}

      {hasExceededMaxSubareaCount && (
        <Alert sx={{ mt: 1 }} severity="error" variant="compact">
          <AlertTitle sx={{ mb: 0 }}>Too many subareas</AlertTitle>
          Projects can be created for up to {CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN} subareas but {selected.length} are currently selected. Please
          reduce the number of subareas selected and try again.
        </Alert>
      )}

      {!allProjectsCreated && (
        <Box sx={{ mt: 1 }}>
          <LoadingButton
            onClick={handleOnClick}
            fullWidth
            variant="contained"
            disabled={loading || hasExceededMaxSubareaCount || selected.length === 0 || overPremCount}
            loading={loading}
          >
            Create {selected.length || ""} subarea project{selected.length === 1 ? "" : "s"}
          </LoadingButton>

          {activeSubscription && subscriptionDetails && (
            <UsageProgress subscription={subscriptionDetails} estimatedQuotaUsage={estimatedQuotaUsage} endTime={activeSubscription.EndTime} />
          )}
        </Box>
      )}
    </>
  );
};

export default CarvePanel;
